import type { EditorTrack } from '../models/editor-track.model';

/**
 * The SINGLE source of truth for whether a track contributes audio — used by BOTH the
 * preview audio sync (preview-sync.service) AND the render-job adapter
 * (editor-project-render-job.adapter) so preview audio can never drift from export audio.
 *
 * Rules (additive — a project with no `solo`/`muted`/`hidden` flags behaves exactly as before):
 *  - SOLO: if ANY track in the project is soloed, ONLY soloed tracks are audible.
 *  - Otherwise a track is audible unless it is `muted`.
 *  - HIDDEN tracks are always silent (a hidden track omits its clips from both preview and export).
 *
 * A soloed track that is also `hidden` stays silent (hidden wins) — hidden means "not in the
 * output at all". A soloed track that is also `muted` stays audible (solo wins over its own mute,
 * matching DAW behaviour where solo overrides the channel mute).
 */
export function isTrackAudible(
  track: Pick<EditorTrack, 'muted' | 'solo' | 'hidden'>,
  allTracks: ReadonlyArray<Pick<EditorTrack, 'solo' | 'hidden'>>
): boolean {
  // Hidden tracks never produce audio.
  if (track.hidden) return false;

  // A soloed track is audible only when it isn't hidden (already handled above).
  const anySolo = allTracks.some(t => t.solo && !t.hidden);
  if (anySolo) return Boolean(track.solo);

  // No solo in play → audible unless explicitly muted.
  return !track.muted;
}
